// apps/main/src/clark-widget/fingerprint.js — RUN AB, §2. The device fingerprint.
//
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// THE LIBRARY, AND ITS LICENCE, BECAUSE THE VERSION IS A TRAP.
//
//   @fingerprintjs/fingerprintjs@5.2.0 — MIT.
//
// 🔴 PIN THE MAJOR. The licence is NOT stable across this package's history and the middle of the
// range is not open source:
//
//     v3.4.2  MIT
//     v4.6.2  BUSL-1.1   ← Business Source License. NOT open source; restricts production use.
//     v5.2.0  MIT
//
// Verified by reading the LICENSE file out of the published tarball, not by trusting the registry's
// `license` field: `npm pack @fingerprintjs/fingerprintjs@5.2.0` → package/LICENSE begins "MIT
// License / Copyright (c) 2025 FingerprintJS, Inc". A `^5.2.0` range cannot drift back into the
// BUSL window, but a well-meaning downgrade to "the version we used to use" would land in it.
//
// This is the free, open-source AGENT — not the paid Fingerprint Pro identification service. The
// visitorId is computed in the browser and travels only to our own server, in the `x-cc-fp` header.
//
// ── 🔴 RUN FP · THE AGENT IS NOT SILENT BY DEFAULT, AND THIS COMMENT SAID IT WAS. ──────────────
// The sentence here used to read "It makes no network call to Fingerprint and sends nothing to
// them." That was FALSE of the code as written, and true only by accident — the import below was
// broken (see §THE IMPORT), so the agent never loaded and therefore never called anything.
//
// MEASURED in node_modules/@fingerprintjs/fingerprintjs/dist/fp.esm.js, function monitor():
//
//     if (window.__fpjs_d_m || Math.random() >= 0.001) return;
//     request.open('get', `https://m1.openfpcdn.io/fingerprintjs/v${version}/npm-monitoring`, true);
//
// `load()` defaults `monitoring: true` and calls monitor(). So a working import ships a GET to
// m1.openfpcdn.io — a FingerprintJS host, carrying the visitor's IP and User-Agent — on ~0.1% of
// homepage loads. `window.__fpjs_d_m` is the *CDN build's* opt-out and is undefined in an npm
// build, so nothing else disarms it.
//
// 🔴 `{ monitoring: false }` BELOW IS THE ONLY THING BETWEEN THIS BUNDLE AND THAT REQUEST, and a
// 0.1% sampled call is invisible to a person watching one page load: 999 loads out of 1000 look
// identical whether the flag is set or not. It is therefore pinned by an assertion on the BUILT
// artifact (test/clarkWidgetFingerprint.test.js), not by anyone remembering to check.
//
// WHAT THE CORRECTED CLAIM IS: with `monitoring: false`, this file makes no network request to any
// FingerprintJS-operated host. The agent reads the local device (canvas, WebGL, audio, fonts,
// screen) and nothing leaves the browser except the visitorId, to us.
//
// ⚠️ RUN MZ3 — RUN TU FOUND THIS INDEPENDENTLY AND ITS PARAGRAPH IS KEPT HERE, WITH ONE CORRECTION.
// TU described the beacon as firing for "every homepage visitor"; FP MEASURED the sampling guard
// above, so the true rate is ~0.1% of loads. The rest of TU's finding stands and is not in FP's
// account — IT WAS LIVE IN PRODUCTION, not introduced by either run. The committed island bundle
// predates a tree where this package was installed at all, so `import()` threw and getFingerprint()
// returned null on a LOCAL build — but ops/deploy/citations/Dockerfile runs `npm run build` INSIDE
// the image, after a full install from package-lock.json, so the DEPLOYED bundle has always carried
// the working agent and its default-on beacon. The defect was found by extending
// servedBytesNoThirdParty.test.js to the committed island bundles, which is the argument for
// extending it: it lives in a bundle the page-scoped HTML scan could not see.
//
// ── 🔴 WHAT IT IS AND IS NOT, SO NOBODY LATER TREATS IT AS A SECURITY BOUNDARY ─────────────────
// The OSS agent's own documentation puts its accuracy far below the paid service, and a value we
// receive in a header is one the caller chose to send. Anyone can send a different one. Its job is
// to make casual evasion — a private window, a cookie clear, a different browser profile on the
// same machine — cost more than one menu click, and to give the per-identity spend share something
// steadier than a cookie to key on. Scripted abuse is Turnstile's job; the bill is the spend
// ceiling's job. See anonTrial.js's identity() for the same statement server-side.
//
// ── PRIVACY ───────────────────────────────────────────────────────────────────────────────────
// 🔴 THIS COLLECTION IS NOT DESCRIBED BY THE CURRENT PRIVACY POLICY. apps/main/public/privacy.html
// §2.4 covers "IP address, browser type, device type, and referring URLs" — data a server RECEIVES
// passively from ordinary request headers. A fingerprint is ACTIVELY PROBED (canvas and WebGL
// rendering, audio stack, font enumeration, screen metrics) and combined into a persistent
// identifier whose PURPOSE is to recognise the same device across visits. §15 covers "essential
// cookies and local storage", and a fingerprint is neither. RUN AB reports this as a disclosure gap
// and does not edit privacy.html, which is outside its scope.
// ═══════════════════════════════════════════════════════════════════════════════════════════════

let _cached = null;
let _inFlight = null;

/**
 * The device fingerprint, computed at most once per page. Returns null rather than throwing.
 *
 * 🔴 IT MUST NEVER BLOCK OR BREAK A TURN. A visitor whose browser refuses the probes (a hardened
 * profile, a privacy extension, an old engine) still gets to ask their question — they simply fall
 * back to the cookie, then the IP, exactly as anonTrial.identity()'s precedence describes. A
 * fingerprint that could deny service by failing would be a worse control than no fingerprint.
 */
export async function getFingerprint(timeoutMs = 2000) {
  if (_cached) return _cached;
  if (_inFlight) return _inFlight;

  _inFlight = (async () => {
    try {
      // ── 🔴 RUN FP · §THE IMPORT. WHY THIS LINE SHIPPED BROKEN FOR THE WHOLE OF RUN AB. ────────
      // The comment here used to say "dynamic import so the agent is fetched off the critical
      // path". That is not what this build does and never was: esbuild.config.mjs bundles the
      // widget to ONE outfile with `splitting` off, so a dynamic import of a resolvable module is
      // inlined into that same file and downloaded with it. What the dynamic form actually defers
      // is EXECUTION of the agent's module factory until the first turn — real, but it is CPU, not
      // bytes, and the byte claim was wrong.
      //
      // 🔴 AND THE `try` AROUND IT IS WHAT MADE THE BREAKAGE SILENT. @fingerprintjs/fingerprintjs
      // was declared in the root package.json by RUN AB but installed nowhere, so esbuild could not
      // resolve this specifier. esbuild normally makes that a hard ERROR — except when the
      // `import()` sits inside a `try`, where it downgrades to a silent pass (0 errors, 0 warnings)
      // and emits the bare specifier VERBATIM. Reproduced in isolation, both shapes, RUN FP §1.
      // The browser then threw "Failed to resolve module specifier" at run time, the `catch` below
      // swallowed it, getFingerprint() returned null on every load, and `x-cc-fp` was absent from
      // every POST — leaving anonTrial.identity() on its LAST tier, the bare IP, for every
      // anonymous visitor. The safety net and the build's silence were the same construct.
      //
      // So the specifier is now genuinely resolvable (the package is installed and in the
      // lockfile), and esbuild.config.mjs asserts after the build that no bare specifier survived
      // in the emitted bundle — because this failure mode produces no error to notice.
      const FingerprintJS = (await import("@fingerprintjs/fingerprintjs")).default;
      // 🔴 `{ monitoring: false }` — NOT optional, and not a tuning knob. See the header: the
      // default is true and it buys a GET to m1.openfpcdn.io on ~0.1% of loads. RUN AB passed no
      // options at all. ⚠️ RUN MZ3 — TWO gates pin this, from two runs that found it separately:
      // test/servedBytesNoThirdParty.test.js asserts this exact CALL SITE, and
      // test/clarkWidgetFingerprint.test.js asserts the BUILT artifact. Removing the flag fails a
      // gate rather than only a policy.
      const agent = await FingerprintJS.load({ monitoring: false });
      const result = await Promise.race([
        agent.get(),
        new Promise((resolve) => setTimeout(() => resolve(null), timeoutMs)),
      ]);
      _cached = result?.visitorId || null;
      return _cached;
    } catch {
      // Blocked, unavailable, or timed out. The server's identity precedence handles it.
      return null;
    } finally {
      _inFlight = null;
    }
  })();
  return _inFlight;
}

export default getFingerprint;
